/// <amd-module name="VSSUI/Components/ItemIndicator/ItemIndicator" />
import * as React from "react";
import { Button } from "VSSUI/Button";
import { css } from "VSSUI/Util";
import { IItemIndicatorProps, IItemIndicatorSetProps } from "../../Components/ItemIndicator/ItemIndicator.Props";
import { IItemState } from "../../Components/ItemIndicator/Item";
import { Icon } from "VSSUI/Icon";
import "VSS/LoaderPlugins/Css!VSSUI/Components/ItemIndicator/ItemIndicator";
import { Tooltip } from "VSSUI/TooltipEx";

export class ItemIndicator extends React.Component<IItemIndicatorProps, IItemState> {
    constructor(props: IItemIndicatorProps) {
        super(props);
        this.state = this._buildNewState(this.props);
    }

    /**
     * This gets executed when a container component re-renders ItemIndicator.
     * @param nextProps New props.
     */
    public componentWillReceiveProps(nextProps: IItemIndicatorProps): void {
        this.setState(this._buildNewState(nextProps));
    }

    public componentDidMount(): void {
        if (this.props.item) {
            this.props.item.subscribe(this._onItemChanged);
        }
    }

    public componentDidUpdate(prevProps: IItemIndicatorProps): void {
        if (this.props.item !== prevProps.item) {
            // If item changed, we need to subscribe for new item
            if (this.props.item) {
                this.props.item.subscribe(this._onItemChanged);
            }

            // We need to unsubscribe prev item
            if (prevProps.item) {
                prevProps.item.unsubscribe(this._onItemChanged);
            }
        }
    }

    public componentWillUnmount(): void {
        if (this.props.item) {
            this.props.item.unsubscribe(this._onItemChanged);
        }
    }

    /**
     * Builds a new state using the specified props.
     */
    private _buildNewState(props: IItemIndicatorProps): IItemState {
        if (props.item) {
            const itemState = props.item.getState();
            return {
                title: itemState.title,
                isPressed: itemState.isPressed,
                iconProps: itemState.iconProps
            };
        } else {
            return {
                title: props.title,
                isPressed: props.isPressed,
                iconProps: props.iconProps
            };
        }
    }

    public render(): JSX.Element {
        let { title, iconProps, isPressed } = this.state;
        const { onClick, className, onRender } = this.props;

        let indicator: JSX.Element;
        let overflowOnly: boolean = false;

        const itemIndicatorClassName = "vss-ItemIndicator flex-noshrink flex-self-center";

        if (onRender) {
            return onRender();
        }

        if (iconProps) {
            title = title || iconProps.title;
            // We need to remove title from props since it will be shown as a tooltip host below
            indicator = (
                <Icon
                    {...{
                        ...iconProps,
                        title: undefined,
                        className: css(iconProps.className, className, !onClick && !title && itemIndicatorClassName)
                    }}
                />
            );
        } else {
            overflowOnly = true;
            indicator = <div className={css(className, !onClick && !title && itemIndicatorClassName)}>{title}</div>;
        }

        if (onClick) {
            indicator = (
                <Button className={css(!title && itemIndicatorClassName)} onClick={this._onClick} ariaLabel={title} ariaPressed={isPressed} tabIndex={0}>
                    {indicator}
                </Button>
            );
        }

        if (title) {
            indicator = (
                <Tooltip text={title} overflowOnly={overflowOnly}>
                    <span className={itemIndicatorClassName}>
                        {indicator}
                    </span>
                </Tooltip>
            );
        }

        return indicator;
    }

    private _onClick = (ev: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        const { item, onClick } = this.props;
        if (onClick) {
            onClick(ev, item);
            ev.preventDefault();
        }
    };

    private _onItemChanged = (state: IItemState) => {
        this.setState({
            title: state.title,
            isPressed: state.isPressed,
            iconProps: state.iconProps
        });
    };
}

export class ItemIndicatorSet extends React.Component<IItemIndicatorSetProps> {
    public render(): JSX.Element | null {
        const { indicators } = this.props;
        if (indicators && indicators.length) {
            const list = indicators.map((indicatorProps, index) => <ItemIndicator {...indicatorProps} key={"ItemIndicator$" + index} />);
            return (
                <div className="vss-IndicatorSet flex-row" onClick={this._onClick}>
                    {list}
                </div>
            );
        }
        return null;
    }

    private _onClick = (ev: React.MouseEvent<HTMLElement>): void => {
        if (!ev.isDefaultPrevented() && this.props.onClick) {
            this.props.onClick(ev);
        }
    };
}
